<script lang="ts" setup>
import { ref, toRaw } from 'vue'
import { useI18n } from 'vue-i18n'

import { AButton, ATextInput } from '../../../main'
import ALoginSuccessMessage from './ALoginSuccessMessage.vue'

type ForgotPasswordFormStatus = 'initial' | 'success' | 'error'

export type ForgotPasswordFormPayload = {
  username: string
  setSuccess: () => void
  setError: () => void
}

export type ResetPasswordFormPayload = {
  username: string
  password: string
  password_confirmation: string
  token: string
}

export interface AForgotPasswordFormProperties {
  loading?: boolean
  payload?: ForgotPasswordFormPayload
  labelUsername?: string | undefined
  labelPlaceholder?: string | undefined
}

export interface ForgotPasswordFormEmits {
  (event: 'submit', data: ForgotPasswordFormPayload): void
  (event: 'cancel'): void
}

defineOptions({ inheritAttrs: false })

const { t } = useI18n({
  messages: {
    en: {
      username: 'Username',
      placeholder: "p.peter{'@'}example.com",
      password: 'Password',
      request: 'Request Password Reset',
      back: 'Back to login',
      success: 'An email has been sent to your email address with further instructions.',
    },
    nl: {
      username: 'Gebruikersnaam',
      placeholder: "p.peter{'@'}voorbeeld.nl",
      password: 'Wachtwoord',
      request: 'Wachtwoordherstel aanvragen',
      back: 'Terug naar inloggen',
      success: 'Er is een e-mail naar je e-mailadres gestuurd met verdere instructies.',
    },
    de: {
      username: 'Benutzername',
      placeholder: "p.peter{'@'}beispiel.de",
      password: 'Passwort',
      request: 'Passwort zurücksetzen',
      back: 'Zurück zur Anmeldung',
      success: 'Eine E-Mail wurde an Ihre E-Mail-Adresse mit weiteren Anweisungen gesendet.',
    },
  },
})

const properties = withDefaults(defineProps<AForgotPasswordFormProperties>(), {
  loading: false,
  labelUsername: undefined,
  labelPlaceholder: undefined,
  payload: () => ({
    username: '',
    setError: () => {},
    setSuccess: () => {},
  }),
})

const emit = defineEmits<ForgotPasswordFormEmits>()
const data = ref<ForgotPasswordFormPayload>(properties.payload)
const status = ref<ForgotPasswordFormStatus>('initial')

function setSuccess(): void {
  status.value = 'success'
}

function setError(): void {
  status.value = 'error'
}

async function onFormSubmit(): Promise<void> {
  emit('submit', { ...toRaw(data.value), setError, setSuccess })
}
</script>

<template>
  <form class="atabix-forgot-password-form" @submit.prevent="onFormSubmit">
    <template v-if="status === 'initial' || status === 'error'">
      <ATextInput
        id="username"
        v-model="data.username"
        :disabled="loading"
        :label="labelUsername === undefined ? t('username') : labelUsername"
        name="username"
        :placeholder="labelPlaceholder === undefined ? t('placeholder') : labelPlaceholder"
        type="text"
      />

      <AButton
        class="atabix-forgot-password-form__button"
        :disabled="loading || data.username.length === 0"
        :loading
        :title="t('request')"
        type="submit"
      />
    </template>

    <template v-if="status === 'success'">
      <ALoginSuccessMessage :message="t('success')" />
    </template>

    <AButton
      class="atabix-forgot-password-form__button"
      :disabled="loading"
      :title="t('back')"
      :variant="status === 'success' ? 'outline' : 'ghost'"
      @click="emit('cancel')"
    />
  </form>
</template>

<style lang="scss" scoped>
.atabix-forgot-password-form {
  @apply grid;
  @apply gap-4;

  .atabix-forgot-password-form__button {
    @apply w-full;
  }
}
</style>
